/* Standard product page (Oct 2026): colour slider (same style, other colourways) and Build your set (same
   colourway). Both read sections/pdp-data.liquid through the search page:
   /search?q=<words>&type=product&section_id=pdp-data. Pre-sale products (template "presale") never show.
   Name rules match snippets/pdp-split.liquid. */
(() => {
  if (window.__eePdp) return;
  window.__eePdp = true;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const searches = {};

  /* ---------- Names: "Raspberry Duo Luxe Sienna Crop" -> colour, fabric, style ---------- */

  // Keep in step with snippets/pdp-split.liquid: add every new two-word colourway to both
  const MULTI = ['Espresso Martini', 'Extra Spicy', 'Morning Sunshine', 'Endless Summer', 'Summer State of Mind', 'Girls Club', 'Wild Child', 'Wild West', 'Electric Butterfly', 'Slice Of Summer', 'Sugar Rush', 'Vacay Mode', 'Merry Woofmas', 'Custom Print', 'Pilates Station', 'Country Club', 'Club Athleticus'];
  const FABRICS = ['Luxe', 'Dynamic', 'Evolve', 'Bamboo', 'Linen Blend'];

  function split(title) {
    const t = (title || '').replace(/\s+/g, ' ').trim();
    const tl = t.toLowerCase();
    let base = MULTI.find((m) => tl.startsWith(`${m.toLowerCase()} `));
    base = base ? t.slice(0, base.length) : t.split(' ')[0];
    let colour = base;
    let rest = t.slice(base.length).trim();
    if (/^duo\s/i.test(rest)) {
      colour = `${base} Duo`;
      rest = rest.slice(3).trim();
    }
    let fabric = '';
    const f = FABRICS.find((x) => rest.toLowerCase().startsWith(`${x.toLowerCase()} `));
    if (f) {
      fabric = f;
      rest = rest.slice(f.length).trim();
    }
    return { base, colour, fabric, style: rest || t, key: `${fabric} ${rest}`.trim().toLowerCase() };
  }

  /* ---------- Shared ---------- */

  // Title-only search, punctuation and one-letter words dropped ("Pocket Curve 9\" Shorts" -> "pocket curve shorts");
  // the exact match happens here, so the search only has to be wide enough. Best matches come first, so the next
  // page (of 50, up to 4) is only fetched while the last result still matches.
  const clean = (words) =>
    words.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(' ').filter((w) => w.length > 1).join(' ');

  function fetchPage(q, page) {
    const url = `${root}search?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last&options%5Bfields%5D=title&page=${page}&section_id=pdp-data`;
    return fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`pdp-data ${r.status}`))))
      .then((html) => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pd-products]');
        return el ? { pages: parseInt(el.dataset.pages, 10) || 1, products: JSON.parse(el.textContent) } : { pages: 1, products: [] };
      });
  }

  function search(words, keep, tag) {
    const q = clean(words);
    if (!q) return Promise.resolve([]);
    const id = `${tag}:${q}`;
    if (!searches[id]) {
      searches[id] = (async () => {
        let all = [];
        for (let page = 1; page <= 4; page += 1) {
          const res = await fetchPage(q, page);
          all = all.concat(res.products);
          const last = res.products[res.products.length - 1];
          if (page >= res.pages || !last || !keep(last)) break;
        }
        return all.filter((p) => !p.presale);
      })();
    }
    return searches[id];
  }

  // Microsoft Clarity custom events (James, 8 Oct): filter sessions by them in Clarity to see which buttons
  // add to bag. Silent when Clarity is not on the page.
  const track = (name) => {
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (e) {
      /* never block a tap on tracking */
    }
  };

  const fill = (template, values) =>
    (template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  function formatMoney(cents) {
    const format = (window.themeVariables && themeVariables.settings && themeVariables.settings.moneyFormat) || '${{amount}}';
    return format.replace(/\{\{\s*amount\w*\s*\}\}/, (cents / 100).toFixed(2));
  }

  // Same flow as Prestige's product form: bundle the cart drawer section so it re-renders with the new item
  async function addToCart(variantId) {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections.join(','), sections_url: `${root}variants/${variantId}` })
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || 'Could not add');
    const cart = await (await fetch(`${root}cart.js`)).json();
    cart.sections = json.sections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'pdp:add', cart } }));
    return cart;
  }

  // Entrance on the next tick; setTimeout (not rAF, starved in in-app browsers) plus a snap to the end state
  function enter(el) {
    el.classList.add('is-entering');
    setTimeout(() => el.classList.remove('is-entering'), 30);
    setTimeout(() => {
      el.style.transition = 'none';
      void el.offsetWidth;
      el.style.transition = '';
    }, 600);
  }

  function smoothScroll(scroller, options) {
    const start = scroller.scrollLeft;
    scroller.scrollTo({ ...options, behavior: reduceMotion ? 'auto' : 'smooth' });
    setTimeout(() => {
      if (scroller.scrollLeft === start) scroller.scrollTo({ ...options, behavior: 'auto' });
    }, 600);
  }

  /* ---------- Colour slider (buy box) ---------- */

  // Every colour of a style runs the same search, so the order is the same on each of them (in stock first)
  function initColours(el) {
    if (el.dataset.pdReady) return;
    el.dataset.pdReady = '1';
    const rail = el.querySelector('[data-pdc-rail]');
    const nameEl = el.querySelector('[data-pdc-name]');
    const countEl = el.querySelector('[data-pdc-count]');
    const prev = el.querySelector('[data-pdc-prev]');
    const next = el.querySelector('[data-pdc-next]');
    const me = split(el.dataset.title);
    if (!rail || !me.key) return;
    const myId = el.dataset.productId;

    const sameStyle = (p) => split(p.title).key === me.key;
    search(me.key, sameStyle, 'style').then((products) => {
      const same = products.filter(sameStyle);
      if (!same.some((p) => String(p.id) !== myId)) return;
      if (!same.some((p) => String(p.id) === myId)) {
        same.unshift({ id: myId, url: el.dataset.url, thumb: el.dataset.image, ratio: parseFloat(el.dataset.ratio) || 0.6667, available: true, title: el.dataset.title });
      }
      const isMe = (p) => String(p.id) === myId;
      const list = [...same.filter((p) => p.available || isMe(p)), ...same.filter((p) => !p.available && !isMe(p))];

      let current = null;
      list.forEach((p) => {
        const colour = split(p.title).colour;
        const li = document.createElement('li');
        li.className = 'pdc__slide';
        const a = document.createElement('a');
        a.className = `pdc__item${isMe(p) ? ' is-current' : ''}${p.available ? '' : ' is-sold'}`;
        a.href = p.url;
        a.setAttribute('aria-label', p.available ? colour : `${colour}, ${el.dataset.soldText || 'Sold out'}`);
        if (isMe(p)) {
          a.setAttribute('aria-current', 'page');
          current = li;
        }
        const src = p.thumb || p.image;
        const img = document.createElement(src ? 'img' : 'span');
        img.className = 'pdc__img';
        img.style.aspectRatio = String(p.ratio || 0.6667);
        if (src) {
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 160;
          img.height = Math.round(160 / (p.ratio || 0.6667));
        }
        a.appendChild(img);
        // The name shows above while hovering or focusing a colour, then goes back to this product's colour
        const show = () => { if (nameEl) nameEl.textContent = colour; };
        const reset = () => { if (nameEl) nameEl.textContent = me.colour; };
        if (!isMe(p)) a.addEventListener('click', () => track('colour_slider_tap'));
        a.addEventListener('mouseenter', show);
        a.addEventListener('focus', show);
        a.addEventListener('mouseleave', reset);
        a.addEventListener('blur', reset);
        li.appendChild(a);
        rail.appendChild(li);
      });

      if (countEl) countEl.textContent = fill(el.dataset.countText, { count: list.length });
      el.hidden = false;

      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        el.classList.toggle('pdc--overflow', max > 2);
        el.classList.toggle('pdc--at-start', rail.scrollLeft <= 2);
        el.classList.toggle('pdc--at-end', rail.scrollLeft >= max - 2);
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max - 2;
      };
      // Start with this product's colour in view
      if (current && current.offsetLeft + current.offsetWidth > rail.clientWidth) {
        rail.scrollLeft = current.offsetLeft - (rail.clientWidth - current.offsetWidth) / 2;
      }
      update();
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      const step = (dir) => {
        smoothScroll(rail, { left: rail.scrollLeft + dir * rail.clientWidth * 0.8 });
        setTimeout(update, 650);
      };
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
    }).catch(() => {});
  }

  /* ---------- Length row (buy box) ---------- */

  // "Black Luxe Pocket Yoga Pants - Tall": the length is the last word of the name, after an optional hyphen.
  // A name without one is Regular. The same colour and style in another length is its own product.
  const LENGTHS = ['Petite', 'Regular', 'Tall'];
  function lengthOf(title) {
    const t = (title || '').replace(/\s+/g, ' ').trim();
    const m = t.match(/^(.+?)\s*(?:[-\u2013\u2014]\s*)?\b(petite|regular|tall)$/i);
    return {
      length: m ? LENGTHS.find((l) => l.toLowerCase() === m[2].toLowerCase()) : 'Regular',
      base: (m ? m[1] : t).toLowerCase(),
    };
  }

  // The size she picked on this page, so another length opens with it picked too. The size gate unticks the
  // server's default size on arrival, so only her own taps count, or a link that named her size
  // (?variant=ID&ee_size=ID, which is also what the length links send)
  const MAIN_PICKER = 'variant-picker[context="main_product"]';
  let pickedSize = '';
  // A size radio's value is Shopify's option value id: the size ("M") is its label's text
  const sizeOf = (radio) => {
    const label = radio.labels && radio.labels[0];
    const text = label && label.querySelector('span:not(.sr-only):not(.block-swatch__color)');
    return (((text || label) && (text || label).textContent) || '').trim();
  };
  // Same checks as the size gate (assets/require-size.js), so a link only carries a size the gate keeps picked
  const deepSize = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const v = q.get('ee_size');
      return v && /^\d+$/.test(v) && v === q.get('variant') ? v : null;
    } catch (e) {
      return null;
    }
  })();

  // A length opens another product, so the tap saves where the row sat on screen and this style's search for the
  // next page. That page stays invisible (inline script in sections/main-product.liquid) until settleArrival() puts
  // the row back on the same spot, then fades in: the swap reads as the same page changing
  const ARRIVE_KEY = 'ee:length';
  const anchorOf = (el) => el.closest('.product-info__block-item') || el;

  function remember(el, a) {
    try {
      const style = el.pdlStyle || { products: [] };
      const target = a.pdlProduct;
      sessionStorage.setItem(ARRIVE_KEY, JSON.stringify({
        path: a.pathname,
        y: Math.round(anchorOf(el).getBoundingClientRect().top),
        t: Date.now(),
        // The next page's length row and colour slider searches, answered already
        searches: [...new Set([style.key, target && split(target.title).key].filter(Boolean).map(clean))],
        products: style.products,
      }));
    } catch (e) {
      /* storage full or blocked: the next page just opens at the top */
    }
  }

  const arrival = (() => {
    try {
      const s = JSON.parse(sessionStorage.getItem(ARRIVE_KEY) || 'null');
      if (!s || s.path !== window.location.pathname || Date.now() - s.t > 15000) return null;
      sessionStorage.removeItem(ARRIVE_KEY);
      if (Array.isArray(s.products)) (s.searches || []).forEach((q) => { searches[`style:${q}`] = Promise.resolve(s.products); });
      return s;
    } catch (e) {
      return null;
    }
  })();

  function settleArrival() {
    const reveal = () => document.documentElement.classList.remove('ee-length-arrive');
    const el = document.querySelector('[data-pd-lengths]');
    if (!el) return reveal();
    const anchor = anchorOf(el);
    const place = () => {
      const delta = anchor.getBoundingClientRect().top - arrival.y;
      if (Math.abs(delta) > 1) window.scrollTo({ top: window.scrollY + delta, behavior: 'instant' });
    };
    place();
    reveal();
    // Hold the row there while photos and anything above it settle, until she touches or scrolls
    const inputs = ['touchstart', 'pointerdown', 'wheel', 'keydown'];
    let timer = null;
    const ro = 'ResizeObserver' in window ? new ResizeObserver(place) : null;
    const end = () => {
      if (ro) ro.disconnect();
      clearTimeout(timer);
      inputs.forEach((type) => window.removeEventListener(type, end, true));
    };
    if (ro) ro.observe(document.body);
    inputs.forEach((type) => window.addEventListener(type, end, { capture: true, passive: true }));
    timer = setTimeout(end, 2500);
  }

  // Fetch the other lengths' pages before the tap (Chrome and Android). Only the page itself is fetched, none of
  // its scripts run, so nothing counts as a visit. Other browsers load it on the tap
  let prefetchRules = null;
  function prefetchLengths(el) {
    if (!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) return;
    const urls = [...el.querySelectorAll('.pdl__item:not([aria-current])')].map((a) => a.href);
    if (prefetchRules) prefetchRules.remove();
    prefetchRules = null;
    if (!urls.length) return;
    prefetchRules = document.createElement('script');
    prefetchRules.type = 'speculationrules';
    prefetchRules.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls }] });
    document.head.appendChild(prefetchRules);
  }

  // A length sold out in her size is struck, and says which size ("Tall, M Sold out")
  function paintLengths(el) {
    const soldText = el.dataset.soldText || 'Sold out';
    el.querySelectorAll('.pdl__item:not([aria-current])').forEach((a) => {
      const p = a.pdlProduct;
      const v = pickedSize ? (p.variants || []).find((x) => x.title === pickedSize) : null;
      const sold = v ? !v.available : !p.available;
      a.classList.toggle('is-disabled', sold);
      a.href = v && v.available ? `${p.url}?variant=${v.id}&ee_size=${v.id}` : p.url;
      let label = a.dataset.length;
      if (sold) label += v ? `, ${pickedSize} ${soldText}` : `, ${soldText}`;
      a.setAttribute('aria-label', label);
    });
    prefetchLengths(el);
  }

  function initLengths(el) {
    if (el.dataset.pdReady) return;
    el.dataset.pdReady = '1';
    const row = el.querySelector('[data-pdl-row]');
    const nameEl = el.querySelector('[data-pdl-name]');
    const me = lengthOf(el.dataset.title);
    const myId = el.dataset.productId;
    if (!row || !me.base) return;

    if (deepSize && !pickedSize) {
      const r = document.querySelector(`${MAIN_PICKER} input[type="radio"][data-sg-variant="${deepSize}"]`);
      if (r && r.checked && !r.disabled && r.dataset.sgAvailable !== '0') pickedSize = sizeOf(r);
    }

    // The colour slider's search (every colour and length of the style): on a Regular page it is the request the
    // slider already made, so most product pages make no extra one
    const key = split(me.base).key;
    const sameKey = (p) => split(lengthOf(p.title).base).key === key;
    const sameStyle = (p) => lengthOf(p.title).base === me.base;
    search(key, sameKey, 'style').then((products) => {
      el.pdlStyle = { key, products: products.filter(sameKey) };
      // One product per length, this one for its own (search only finds in stock products, so add it when sold out)
      const byLength = { [me.length]: { id: myId, url: el.dataset.url } };
      products.filter(sameStyle).forEach((p) => {
        const { length } = lengthOf(p.title);
        if (length !== me.length && !byLength[length]) byLength[length] = p;
      });
      const list = LENGTHS.filter((l) => byLength[l]);
      if (list.length < 2) return;

      list.forEach((length) => {
        const p = byLength[length];
        const a = document.createElement('a');
        a.className = 'block-swatch pdl__item';
        a.href = p.url;
        a.dataset.length = length;
        const label = document.createElement('span');
        label.textContent = length;
        a.appendChild(label);
        if (String(p.id) === myId) {
          a.classList.add('is-selected');
          a.setAttribute('aria-current', 'page');
          // Tapping this length again would reload the page and drop her size
          a.addEventListener('click', (event) => event.preventDefault());
        } else {
          a.pdlProduct = p;
          a.addEventListener('click', () => {
            track('length_row_tap');
            remember(el, a);
          });
        }
        row.appendChild(a);
      });
      if (nameEl) nameEl.textContent = me.length;
      // Height guide (theme editor switch): only the lengths this style comes in, Regular left out
      const guide = el.querySelector('[data-pdl-guide]');
      if (guide) {
        guide.textContent = list
          .filter((length) => length !== 'Regular' && (guide.dataset[length.toLowerCase()] || '').trim())
          .map((length) => `${length} ${guide.dataset[length.toLowerCase()].trim()}.`)
          .join(' ');
        guide.hidden = !guide.textContent;
      }
      paintLengths(el);
      el.hidden = false;
    }).catch(() => {});
  }

  document.addEventListener('change', (event) => {
    const r = event.target;
    if (!r || !r.matches || !r.matches(`${MAIN_PICKER} input[type="radio"]`)) return;
    pickedSize = sizeOf(r);
    document.querySelectorAll('[data-pd-lengths][data-pd-ready]').forEach(paintLengths);
  }, true);

  /* ---------- Build your set ---------- */

  const PLUS_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  const TICK_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5.5 12.5 4 4 9-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Resolves once the element is within ~2000px of the screen (a fast scroll still finds it filled) (straight away where IntersectionObserver is missing)
  function whenNear(el) {
    return new Promise((resolve) => {
      if (!('IntersectionObserver' in window)) {
        resolve();
        return;
      }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          io.disconnect();
          resolve();
        }
      }, { rootMargin: '2000px 0px' });
      io.observe(el);
    });
  }

  function parseFilters(spec) {
    return (spec || '').split('|').map((part) => {
      const [name, words] = part.split(':');
      return name && words ? { name: name.trim(), words: words.split(',').map((w) => w.trim().toLowerCase()).filter(Boolean) } : null;
    }).filter(Boolean);
  }

  function initSet(section) {
    const grid = section.querySelector('[data-pd-grid]');
    const rail = section.querySelector('[data-pd-rail]');
    const filtersEl = section.querySelector('[data-pd-filters]');
    const progress = section.querySelector('[data-pd-progress]');
    const prev = section.querySelector('[data-pd-rail-prev]');
    const next = section.querySelector('[data-pd-rail-next]');
    const sheet = section.querySelector('[data-pd-sheet]');
    const toast = section.querySelector('[data-pd-toast]');
    const ds = section.dataset;
    if (!grid || !rail || !ds.colour) return;

    const narrow = (ds.narrow || '').split('|').map((x) => x.trim().toLowerCase()).filter(Boolean);
    const sameFabric = narrow.includes(ds.colour.toLowerCase()) && ds.fabric;

    /* Rail: progress line + desktop arrows */
    const updateRail = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      const visible = max > 0 ? rail.clientWidth / rail.scrollWidth : 1;
      const ratio = max > 0 ? rail.scrollLeft / max : 0;
      if (progress) {
        const width = Math.max(visible, 0.12);
        progress.style.width = `${width * 100}%`;
        progress.style.transform = `translateX(${ratio * ((1 - width) / width) * 100}%)`;
      }
      if (prev) prev.disabled = rail.scrollLeft <= 4;
      if (next) next.disabled = rail.scrollLeft >= max - 4;
    };
    rail.addEventListener('scroll', updateRail, { passive: true });
    window.addEventListener('resize', updateRail);
    const step = (dir) => {
      smoothScroll(rail, { left: rail.scrollLeft + dir * rail.clientWidth * 0.85 });
      setTimeout(updateRail, 650);
    };
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));

    /* Toast */
    let toastTimer;
    const showToast = (title, cart) => {
      if (!toast) return;
      toast.querySelector('[data-pd-toast-title]').textContent = title;
      const ship = toast.querySelector('[data-pd-toast-ship]');
      if (cart) {
        const threshold = parseInt(ds.freeShip, 10) || 0;
        const line = fill(ds.labelCount, { count: cart.item_count, total: formatMoney(cart.total_price) });
        const left = threshold - cart.total_price;
        const shipLine = threshold ? (left > 0 ? fill(ds.labelShipLeft, { left: formatMoney(left) }) : ds.labelShipDone) : '';
        ship.textContent = shipLine ? `${line} · ${shipLine}` : line;
      } else {
        ship.textContent = '';
      }
      if (toast.hidden) {
        toast.hidden = false;
        enter(toast);
      }
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
    };

    const cartLink = toast && toast.querySelector('[data-pd-toast-cart]');
    if (cartLink) {
      cartLink.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          event.preventDefault();
          toast.hidden = true;
          drawer.show();
        }
      });
    }

    const flashAdded = (quick) => {
      if (!quick) return;
      quick.classList.add('is-added');
      quick.innerHTML = TICK_ICON;
      setTimeout(() => {
        quick.classList.remove('is-added');
        quick.innerHTML = PLUS_ICON;
      }, 2200);
    };

    /* Size sheet */
    let sheetVariant = null;
    let sheetQuick = null;
    const sheetSizes = sheet && sheet.querySelector('[data-pd-sheet-sizes]');
    const sheetAdd = sheet && sheet.querySelector('[data-pd-sheet-add]');
    const sheetError = sheet && sheet.querySelector('[data-pd-sheet-error]');
    const sheetTitle = sheet && sheet.querySelector('[data-pd-sheet-title]');

    const setSheetButton = () => {
      sheetAdd.disabled = !sheetVariant;
      sheetAdd.textContent = sheetVariant ? `${ds.labelAdd} · ${sheetVariant.title}` : ds.labelPick;
    };

    const openSheet = (p, name, quick) => {
      sheetVariant = null;
      sheetQuick = quick;
      const img = sheet.querySelector('[data-pd-sheet-img]');
      img.style.aspectRatio = String(p.ratio || 0.6667);
      if (p.image) {
        img.src = p.image;
        img.hidden = false;
      } else {
        img.hidden = true;
      }
      sheetTitle.textContent = name;
      sheet.querySelector('[data-pd-sheet-price]').textContent = p.price;
      sheet.querySelector('[data-pd-sheet-link]').href = p.url;
      sheetError.hidden = true;
      sheetSizes.innerHTML = '';
      p.variants.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pds-size';
        b.textContent = v.title;
        b.disabled = !v.available;
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', () => {
          sheetSizes.querySelectorAll('.pds-size').forEach((other) => other.setAttribute('aria-pressed', 'false'));
          b.setAttribute('aria-pressed', 'true');
          sheetVariant = v;
          setSheetButton();
        });
        sheetSizes.appendChild(b);
      });
      sheetSizes.style.setProperty('--pd-size-count', String(Math.min(p.variants.length, 7)));
      setSheetButton();
      sheet.showModal();
      enter(sheet);
    };

    if (sheet) {
      sheet.querySelector('[data-pd-sheet-close]').addEventListener('click', () => sheet.close());
      sheet.addEventListener('click', (event) => {
        if (event.target === sheet) sheet.close();
      });
      sheetAdd.addEventListener('click', async () => {
        if (!sheetVariant || sheetAdd.getAttribute('aria-busy') === 'true') return;
        sheetAdd.setAttribute('aria-busy', 'true');
        sheetError.hidden = true;
        try {
          const cart = await addToCart(sheetVariant.id);
          track('set_add_to_cart');
          const title = fill(ds.labelAdded, { name: `${sheetTitle.textContent} · ${sheetVariant.title}` });
          sheet.close();
          flashAdded(sheetQuick);
          showToast(title, cart);
        } catch (e) {
          sheetError.textContent = e.message;
          sheetError.hidden = false;
        } finally {
          sheetAdd.removeAttribute('aria-busy');
        }
      });
    }

    const words = sameFabric ? `${ds.colour} ${ds.fabric}` : ds.colour;
    const inSet = (p) => {
      const s = split(p.title);
      if (s.base.toLowerCase() !== ds.colour.toLowerCase()) return false;
      return !sameFabric || s.fabric.toLowerCase() === ds.fabric.toLowerCase();
    };
    // The set sits far below the buy box: search the catalogue when it scrolls near, not on page load.
    // The set block stays hidden until it has results, so watch its section wrapper instead.
    // Each length of a style is its own product: offer each style once, in this page's length where there is one
    // (else Regular), and never this style in another length
    const mine = lengthOf(ds.title);
    const rankOf = (length) => (length === mine.length ? 0 : length === 'Regular' ? 1 : 2);
    whenNear(section.closest('.shopify-section') || section.parentElement).then(() => search(words, inSet, 'set')).then((products) => {
      const limit = parseInt(ds.limit, 10) || 16;
      const best = {};
      products.forEach((p) => {
        if (String(p.id) === ds.productId || !p.available || !inSet(p)) return;
        const l = lengthOf(p.title);
        if (l.base === mine.base) return;
        if (!best[l.base] || rankOf(l.length) < rankOf(lengthOf(best[l.base].title).length)) best[l.base] = p;
      });
      const matches = products.filter((p) => best[lengthOf(p.title).base] === p);
      if (!matches.length) return;

      // Product names end in the noun ("Pocket Yoga Pants"), so the group whose word appears last wins
      const groups = parseFilters(ds.filters);
      const groupOf = (title) => {
        const t = title.toLowerCase();
        let best = '';
        let bestAt = -1;
        groups.forEach((group) => {
          group.words.forEach((w) => {
            const at = t.lastIndexOf(w);
            if (at > bestAt) {
              bestAt = at;
              best = group.name;
            }
          });
        });
        return best;
      };

      // The other half of the outfit first (a crop shows bottoms first, leggings show tops first),
      // single-size extras (scrunchies, headbands) last
      const own = groupOf(ds.title || '');
      const pair = { Tops: 'Bottoms', Bottoms: 'Tops' }[own];
      const rank = (p) => {
        if (p.variants.length < 2) return 3;
        const g = groupOf(p.title);
        return g === pair ? 0 : g === own ? 2 : 1;
      };
      const list = matches.sort((a, b) => rank(a) - rank(b)).slice(0, limit);

      list.forEach((p) => {
        const name = split(p.title).style;
        const card = document.createElement('li');
        card.className = 'pds-card-p';
        card.dataset.group = groupOf(p.title);
        card.innerHTML = `
          <div class="pds-card-p__media" style="aspect-ratio: ${p.ratio || 0.6667}">
            <a class="pds-card-p__link" href="${p.url}" tabindex="-1" aria-hidden="true">
              ${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="600" height="900">` : ''}
              ${p.image2 ? `<img src="${p.image2}" alt="" loading="lazy" width="600" height="900">` : ''}
            </a>
            <button type="button" class="pds-card-p__quick">${PLUS_ICON}</button>
          </div>
          <a class="pds-card-p__title" href="${p.url}"></a>
          <p class="pds-card-p__price"></p>`;
        card.querySelector('.pds-card-p__title').textContent = name;
        card.querySelector('.pds-card-p__price').textContent = p.price;

        const quick = card.querySelector('.pds-card-p__quick');
        quick.setAttribute('aria-label', fill(ds.labelQuick, { name }));
        const single = p.variants.length === 1 ? p.variants[0] : null;

        quick.addEventListener('click', async () => {
          if (!single) {
            if (sheet && typeof sheet.showModal === 'function') openSheet(p, name, quick);
            else window.location.href = p.url;
            return;
          }
          if (quick.getAttribute('aria-busy') === 'true') return;
          quick.setAttribute('aria-busy', 'true');
          try {
            const cart = await addToCart(single.id);
            track('set_add_to_cart');
            flashAdded(quick);
            showToast(fill(ds.labelAdded, { name }), cart);
          } catch (e) {
            showToast(e.message, null);
          } finally {
            quick.removeAttribute('aria-busy');
          }
        });

        grid.appendChild(card);
      });

      // Filter chips: only groups that have something in them
      const used = groups.filter((g) => list.some((p) => groupOf(p.title) === g.name));
      if (filtersEl && used.length > 1) {
        const chips = [{ name: ds.labelAll || 'All' }, ...used].map((g, i) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'pds-chip';
          chip.setAttribute('role', 'tab');
          chip.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
          chip.textContent = g.name;
          chip.addEventListener('click', () => {
            chips.forEach((c) => c.setAttribute('aria-selected', 'false'));
            chip.setAttribute('aria-selected', 'true');
            grid.querySelectorAll('.pds-card-p').forEach((c) => {
              c.hidden = i !== 0 && c.dataset.group !== g.name;
            });
            rail.scrollLeft = 0;
            updateRail();
          });
          filtersEl.appendChild(chip);
          return chip;
        });
      } else if (filtersEl) {
        filtersEl.remove();
      }

      section.hidden = false;
      updateRail();
    }).catch(() => {});
  }

  /* ---------- Which buy button was used ---------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('button[type="submit"]');
    if (!btn || btn.closest('notify-me')) return;
    const gated = btn.classList.contains('size-gate-btn') || btn.getAttribute('data-size-gated') === '1';
    if (btn.closest('product-sticky-bar')) {
      track(gated ? 'sticky_select_size' : 'sticky_add_to_cart');
    } else if (btn.closest('#quick-buy-modal-content, quick-buy-modal')) {
      if (!gated && !btn.disabled) track('quick_buy_add_to_cart');
    } else if (btn.closest('.product-info__block-item[data-block-type="buy-buttons"]')) {
      if (!gated && !btn.disabled) track('main_add_to_cart');
    }
  }, true);

  /* ---------- Description: headline + intro, the rest folded into tabs (James, 8 Oct) ----------
     Every description follows the same shape: feature icons, a catchy heading, an intro, the size line, the
     bullet list, then "Discover the Empress difference" (B's fit notes). The icons, heading and intro stay;
     everything from the size line on becomes a Details tab, and each later heading its own tab, styled like
     the Sizing Guide / Shipping / Returns tabs that follow. Without JavaScript the full description shows. */

  const KEEP_CAPS = ['OMG', 'AU', 'XS', 'UPF', 'NBCF', 'B', '1X', '2X', '3X'];
  const PROPER = ['empress', 'australia', 'australian', 'cleo', 'sienna', 'raya', 'zara', 'vida', 'nova', 'bolero'];

  // "STYLE THAT SUPPORTS YOU" -> "Style that supports you", "RIDE. RUN. LOUNGE" -> "Ride. Run. Lounge"
  function sentenceCase(text) {
    let start = true;
    return text.trim().toLowerCase().split(/(\s+)/).map((word) => {
      if (/^\s+$/.test(word)) return word;
      const bare = word.replace(/[^a-z0-9]/g, '');
      let out = word;
      if (KEEP_CAPS.includes(bare.toUpperCase())) out = word.toUpperCase();
      else if (start || PROPER.includes(bare)) out = word.replace(/[a-z]/, (c) => c.toUpperCase());
      start = /[.!?]$/.test(word);
      return out;
    }).join('');
  }

  const TAB_ICONS = {
    details: '<path d="M3.5 12.5V4.5a1 1 0 0 1 1-1h8l8 8.5-8.5 8.5-8.5-8Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><circle cx="8" cy="8" r="1.4" fill="currentColor"/>',
    crown: '<path d="M3.5 8.5 7.5 12 12 5l4.5 7 4-3.5-1.8 9.5H5.3L3.5 8.5Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>',
    model: '<path d="M3 8.5h18v7H3zM7 8.5v3M11 8.5v4M15 8.5v3M19 8.5v4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>',
    sparkle: '<path d="M12 3.5c.6 4.3 2.4 6.6 7 7.5-4.6.9-6.4 3.2-7 7.5-.6-4.3-2.4-6.6-7-7.5 4.6-.9 6.4-3.2 7-7.5Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>'
  };

  function initDescription(prose) {
    if (prose.dataset.pdReady) return;
    prose.dataset.pdReady = '1';
    const kids = [...prose.children];
    const isHeading = (el) => /^H[1-6]$/.test(el.tagName);
    const isList = (el) => el.tagName === 'UL' || el.tagName === 'OL';
    const isSize = (el) => el.tagName === 'P' && /^size.?inclusive/i.test(el.textContent.trim());

    let i = 0;
    while (i < kids.length && kids[i].classList.contains('product-icons')) i += 1;
    if (i < kids.length && isHeading(kids[i])) {
      const headline = kids[i];
      headline.classList.add('pdd-headline');
      headline.textContent = sentenceCase(headline.textContent);
      i += 1;
    }
    while (i < kids.length && !isHeading(kids[i]) && !isList(kids[i]) && !isSize(kids[i])) i += 1;
    if (i >= kids.length) return;

    const groups = [];
    let current = { title: 'Details', icon: 'details', els: [] };
    kids.slice(i).forEach((el) => {
      if (isHeading(el)) {
        if (current.els.length) groups.push(current);
        const title = el.textContent.replace(/\s+/g, ' ').trim();
        current = { title, icon: /empress/i.test(title) ? 'crown' : 'sparkle', els: [] };
        el.remove();
      } else {
        current.els.push(el);
      }
    });
    if (current.els.length) groups.push(current);

    // "Discover the Empress difference" holds the model's measurements (James: call it Model fit). Fabric specs,
    // Oeko-Tex and the underwear hygiene note go to the end of Details instead. If there is no model info under
    // it, the heading keeps its own title.
    groups.forEach((group) => {
      if (!/empress difference/i.test(group.title)) return;
      const toDetails = group.els.filter((el) => /^(fabric spec|oeko|please note)/i.test(el.textContent.trim()));
      if (toDetails.length) {
        let details = groups.find((g) => g.icon === 'details');
        if (!details) {
          details = { title: 'Details', icon: 'details', els: [] };
          groups.unshift(details);
        }
        details.els.push(...toDetails);
        group.els = group.els.filter((el) => !toDetails.includes(el));
      }
      if (group.els.some((el) => /(model|wears|wearing|height)/i.test(el.textContent))) {
        group.title = 'Model fit';
        group.icon = 'model';
      }
    });
    const tabsToBuild = groups.filter((g) => g.els.length);
    if (!tabsToBuild.length) return;

    const tabs = document.createElement('div');
    tabs.className = 'pdd-tabs accordion-group';
    tabsToBuild.forEach((group) => {
      const item = document.createElement('accordion-disclosure');
      item.className = 'accordion';
      item.innerHTML = `
        <details class="accordion__disclosure group" aria-expanded="false">
          <summary>
            <span class="accordion__toggle h6">
              <span class="text-with-icon gap-4"><svg class="pdd-tab-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">${TAB_ICONS[group.icon]}</svg><span data-pdd-title></span></span>
              <span class="animated-plus group-expanded:rotate" aria-hidden="true"></span>
            </span>
          </summary>
          <div class="accordion__content prose"></div>
        </details>`;
      item.querySelector('[data-pdd-title]').textContent = group.title;
      const content = item.querySelector('.accordion__content');
      group.els.forEach((el) => content.appendChild(el));
      const wrap = document.createElement('div');
      wrap.appendChild(item);
      tabs.appendChild(wrap);
    });
    prose.after(tabs);
    prose.closest('.product-info__block-item')?.classList.add('has-pdd-tabs');
  }

  /* ---------- Boot ---------- */

  const boot = () => {
    document.querySelectorAll('[data-pd-colours]').forEach(initColours);
    document.querySelectorAll('[data-pd-lengths]').forEach(initLengths);
    document.querySelectorAll('.ee-pdp .product-info__block-item[data-block-type="description"] > .prose').forEach(initDescription);
    document.querySelectorAll('[data-pd-set]').forEach((s) => {
      if (s.dataset.pdReady) return;
      s.dataset.pdReady = '1';
      initSet(s);
    });
  };
  const start = () => {
    boot();
    if (arrival) settleArrival();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  // Prestige re-renders the buy box on a size change: the colour slider and length row come back empty, so fill them again
  // (the search is cached, so this is instant)
  document.addEventListener('variant:change', () => setTimeout(boot, 50));
  document.addEventListener('product:rerender', () => setTimeout(boot, 50));
  // Theme editor: a settings change re-renders the section empty, so fill it again
  document.addEventListener('shopify:section:load', () => setTimeout(boot, 50));
})();
